import { useState, useEffect } from 'react'
import { supabase } from '../api/supabase'

export interface AppUser {
  id: string
  user_email: string
  full_name: string | null
  role: string // legacy, mirrored from roles[0] by a DB trigger — read-only, don't write it
  roles: string[]
  department: string | null
  branch: string
  initials: string | null
  is_active: boolean
  created_at: string
  group_id: string | null
  // #290: mapping to BoxTop's Owner field + e-Customs (Descartes) login. boxtop_owner_name
  // is a plain text field an admin types in directly (e.g. "Gary Liu") — no lookup table.
  boxtop_owner_name: string | null
  ecustoms_username: string | null
  ecustoms_password: string | null // plaintext by explicit choice — matches BoxTop's own practice
}

export function useUsers() {
  const [users, setUsers] = useState<AppUser[]>([])
  const [loading, setLoading] = useState(false)

  const fetchUsers = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('user_roles')
      .select('*')
      .order('created_at', { ascending: false })
    setUsers(data || [])
    setLoading(false)
  }

  useEffect(() => { fetchUsers() }, [])

  // E: previously discarded any Supabase error on both branches -- a duplicate-email insert
  // (user_roles.user_email is UNIQUE) would fail silently with no feedback to the caller.
  const saveUser = async (user: Partial<AppUser>) => {
    if (user.id) {
      const { error } = await supabase.from('user_roles').update(user).eq('id', user.id)
      if (!error) await fetchUsers()
      return { error }
    }
    // New row: strip empty-string fields (e.g. a blank-form default like
    // created_at: '') so Postgres column defaults apply instead of failing
    // to parse '' as a timestamp/date.
    const insertPayload = Object.fromEntries(
      Object.entries(user).filter(([, v]) => v !== '')
    )
    const { error } = await supabase.from('user_roles').insert(insertPayload)
    if (!error) await fetchUsers()
    return { error }
  }

  const toggleActive = async (id: string, isActive: boolean) => {
    await supabase.from('user_roles').update({ is_active: isActive }).eq('id', id)
    await fetchUsers()
  }

  return { users, loading, saveUser, toggleActive, refetch: fetchUsers }
}
